import React, { useState } from 'react'
import { RadioGroup, RadioGroupItem, Button } from '@shopflo/ui'
import { Eye, EyeSlash } from '@phosphor-icons/react'
import { classNames } from 'src/lib/utils/helpers'
import FloCheckbox from 'src/components/common/form-elements/FloCheckbox'
import { useLocale } from 'src/lib/hooks/useLocal'
import { useExitNudge } from 'src/lib/contexts/ExitNudgeContext'

import {
  ExitCouponExpanded,
  ExitNudgeOption,
  UrgencyNudgeExpanded,
  UrgencyNudgeErrors
} from './ExitCouponNudgeExpandedSections'

interface ExitCouponTileProps {
  canEdit?: boolean
}

export const ExitCouponTile: React.FC<ExitCouponTileProps> = ({ canEdit = true }) => {
  const { t } = useLocale()
  const { state, setIsEnabled, setSelectedOption, setExitCoupon, setUrgency, showPreview, setShowPreview, exitCouponErrors, setExitCouponErrors } = useExitNudge()
  const { isEnabled, selectedOption, exitCoupon, urgency } = state
  const [urgencyErrors, setUrgencyErrors] = useState<UrgencyNudgeErrors>({})

  const NUDGE_OPTIONS: { id: ExitNudgeOption; title: string; subtitle: string }[] = [
    {
      id: 'SURVEY',
      title: t('exit_nudge.survey_title'),
      subtitle: t('exit_nudge.survey_subtitle')
    },
    {
      id: 'EXIT_CONFIRMATION',
      title: t('exit_nudge.exit_confirmation_title'),
      subtitle: t('exit_nudge.exit_confirmation_subtitle')
    },
    {
      id: 'EXIT_COUPON_NUDGE',
      title: t('exit_nudge.exit_coupon_nudge_title'),
      subtitle: t('exit_nudge.exit_coupon_nudge_subtitle')
    },
    {
      id: 'URGENCY_NUDGE',
      title: t('exit_nudge.urgency_nudge_title'),
      subtitle: t('exit_nudge.urgency_nudge_subtitle')
    }
  ]

  const isInteractive = canEdit && isEnabled

  return (
    <>
    <div className='relative bg-white rounded-lg border border-black/[0.06] p-6 flex flex-row gap-6'>
      <div className='flex-1 min-w-0 flex flex-col'>
        {/* Header */}
        <div className='flex flex-row justify-between items-start mb-5'>
          <div>
            <h3 className='text-text-md-semibold text-zinc-900'>{t('exit_nudge.title')}</h3>
            <p className='text-text-xs-regular text-zinc-500 mt-0.5'>{t('exit_nudge.subtitle')}</p>
          </div>
          <div className='flex items-center gap-2 flex-shrink-0'>
            {isEnabled && (
              <Button
                variant='ghost'
                size='sm'
                leftIcon={showPreview ? EyeSlash : Eye}
                onClick={() => setShowPreview(!showPreview)}
              >
                {showPreview ? t('exit_nudge.hide_preview') : t('exit_nudge.show_preview')}
              </Button>
            )}
            <FloCheckbox
              isDisabled={!canEdit}
              type='SWITCH'
              value={isEnabled}
              setValue={(checked: boolean) => {
              setIsEnabled(checked)
            }}
            />
          </div>
        </div>

        {/* Option rows */}
        <RadioGroup
          value={selectedOption}
          onValueChange={v => isInteractive && setSelectedOption(v as ExitNudgeOption)}
          className='flex flex-col gap-3'
        >
          {NUDGE_OPTIONS.map(option => {
            const isChecked = selectedOption === option.id

            return (
              <div
                key={option.id}
                className={classNames('flex flex-row gap-3 items-start', !isEnabled ? 'opacity-40' : '')}
              >
                <div className='pt-0.5 flex-shrink-0'>
                  <RadioGroupItem value={option.id} id={option.id} disabled={!isInteractive} />
                </div>

                <div className='flex-1 flex flex-col gap-3 min-w-0'>
                  <label
                    htmlFor={option.id}
                    className={classNames(
                      'flex flex-col',
                      isInteractive ? 'cursor-pointer' : 'cursor-not-allowed'
                    )}
                  >
                    <span className='text-text-sm-medium text-zinc-900'>{option.title}</span>
                    <span className='text-text-xs-regular text-zinc-500'>{option.subtitle}</span>
                  </label>

                  {isChecked && option.id === 'EXIT_COUPON_NUDGE' && (
                    <ExitCouponExpanded config={exitCoupon} onChange={v => { setExitCoupon(v); setExitCouponErrors({}) }} canEdit={isInteractive} errors={exitCouponErrors} />
                  )}

                  {isChecked && option.id === 'URGENCY_NUDGE' && (
                    <UrgencyNudgeExpanded config={urgency} onChange={v => { setUrgency(v); setUrgencyErrors({}) }} canEdit={isInteractive} errors={urgencyErrors} />
                  )}
                </div>
              </div>
            )
          })}
        </RadioGroup>
      </div>
    </div>
    </>
  )
}
